<template>
  <div class="mt-6 px-4" style="width: 98%;">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">Workflows</h1>
        <h1 class="text text-gray-900 dark:text-gray-200">Build and manage automation workflows.</h1>
      </div>

      <div class="mt-3 lg:mt-0 flex flex-col sm:flex-row sm:items-center gap-3">
        <label class="input input-bordered flex items-center" style="width:330px">
          <input
            type="text"
            v-model="searchValue"
            class="grow"
            placeholder="Search"
            maxlength="200"
            autocomplete="off"
            autocapitalize="none"
            spellcheck="false"
          />
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
            <path
              fill-rule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clip-rule="evenodd"
            />
          </svg>
        </label>
        <button class="btn" @click="handleCreateWorkflowClick">
          {{ "Create New Workflow" }}
          <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>

  <div class="mt-10 w-full">
    <ThemeOnly theme="modern" :invert="true">
      <div
        class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex"
        style="border-radius: 10px; width:100%;height: 70vh;"
      >
        <EasyDataTable
          :headers="headers"
          :items="filteredWorkflows"
          :loading="isLoading"
          buttons-pagination
          :search-field="searchFields"
          :search-value="searchValue"
        >
          <template #loading>
            <ModernLoader />
          </template>
          <template #item-isactive="item">
            <span :class="item.isactive === true ? 'status-pill status-active' : 'status-pill status-disabled'">
              {{ item.isactive === true ? "Active" : "Disabled" }}
            </span>
          </template>

          <template #item-updatedat="item">
            {{ formatDate(item !== null && item !== undefined ? item.updatedat : undefined) }}
          </template>

          <template #item-options="item">
            <button @click="handleEditClick(item)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Edit</button>
            <button @click="handleDeleteClick(item)" class="hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Delete</button>
          </template>
        </EasyDataTable>
      </div>
    </ThemeOnly>

    <ThemeOnly theme="modern">
      <ModernAgentTable
        :headers="headers"
        :items="filteredWorkflows"
        :loading="isLoading"
      >
        <template #item-isactive="{ item }">
          <span :class="item.isactive === true ? 'status-pill status-active' : 'status-pill status-disabled'">
            {{ item.isactive === true ? "Active" : "Disabled" }}
          </span>
        </template>

        <template #item-updatedat="{ item }">
          {{ formatDate(item.updatedat) }}
        </template>

        <template #item-options="{ item }">
          <div class="flex items-center justify-end">
            <button @click="handleEditClick(item)" class="action-btn btn-edit">
              <Icon icon="tabler:edit" width="16" />
              <span class="btn-text">Edit</span>
            </button>
            <button @click="handleDeleteClick(item)" class="action-btn btn-delete">
              <Icon icon="tabler:trash" width="16" />
              <span class="btn-text">Delete</span>
            </button>
          </div>
        </template>
      </ModernAgentTable>
    </ThemeOnly>
  </div>
  </div>

  <div class="modal" :class="{ 'modal-open': isDeleteModalOpen === true }">
    <div class="modal-box">
      <h3 class="font-bold text-lg">Delete workflow?</h3>
      <p class="py-4">Are you sure you want to delete this workflow?</p>
      <div class="modal-action">
        <button @click="isDeleteModalOpen = false" class="btn">Cancel</button>
        <button @click="deleteWorkflowConfirmed" class="btn btn-error">Delete</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="isDeleteModalOpen = false">close</button>
    </form>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import axios from "@/api/axiosInstance";
import router from "@/router";
import { globalState } from "@/globalState";
import ThemeOnly from "@/components/ThemeOnly.vue";
import ModernAgentTable from "@/components/ModernAgentTable.vue";
import ModernLoader from "@/components/ModernLoader.vue";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

const toast = useToast();
const apiEndpoint = computed(() => globalState.apiEndpoint);

const isLoading = ref(false);
const workflows = ref([]);
const searchValue = ref("");
const isDeleteModalOpen = ref(false);
const selectedToDeleteId = ref(null);
const searchFields = ["workflowid", "workflowname", "workflowguid"];

const headers = [
  { text: "Workflow ID", value: "workflowid", sortable: true },
  { text: "Workflow Name", value: "workflowname", sortable: true },
  { text: "Webhook GUID", value: "workflowguid", sortable: true },
  { text: "Status", value: "isactive", sortable: true },
  { text: "Updated At", value: "updatedat", sortable: true },
  { text: "Options", value: "options" }
];

function safeString(value) {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

function normalizeWorkflowRow(row) {
  const safeRow = row !== null && typeof row === "object" ? row : {};
  return {
    workflowid: safeRow.workflowid !== undefined ? safeRow.workflowid : safeRow.workflowId,
    workflowguid: safeString(safeRow.workflowguid !== undefined ? safeRow.workflowguid : safeRow.workflowGuid),
    workflowname: safeString(safeRow.workflowname !== undefined ? safeRow.workflowname : safeRow.workflowName),
    isactive: safeRow.isactive !== false && safeRow.isActive !== false,
    updatedat: safeRow.updatedat !== undefined ? safeRow.updatedat : safeRow.updatedAt
  };
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) === true) {
    return "";
  }
  return date.toLocaleString();
}

const filteredWorkflows = computed(() => {
  const query = searchValue.value.trim().toLowerCase();
  if (query.length === 0) {
    return workflows.value;
  }
  return workflows.value.filter((workflow) => {
    return safeString(workflow.workflowid).toLowerCase().includes(query)
      || safeString(workflow.workflowname).toLowerCase().includes(query)
      || safeString(workflow.workflowguid).toLowerCase().includes(query);
  });
});

async function loadWorkflows() {
  isLoading.value = true;
  try {
    const response = await axios.get(`${apiEndpoint.value}/api/workflows/list`);
    const rawItems = Array.isArray(response.data?.workflows)
      ? response.data.workflows
      : (Array.isArray(response.data) ? response.data : []);
    workflows.value = rawItems.map(normalizeWorkflowRow);
  } catch (error) {
    toast.error("Failed to load workflows.");
  } finally {
    isLoading.value = false;
  }
}

function handleDeleteClick(workflow) {
  selectedToDeleteId.value = workflow?.workflowid;
  isDeleteModalOpen.value = true;
}

function handleEditClick(workflow) {
  const workflowId = Number.parseInt(safeString(workflow?.workflowid), 10);
  if (Number.isFinite(workflowId) !== true || workflowId <= 0) {
    return;
  }
  router.push({ path: "/workflowEditor", query: { id: String(workflowId) } });
}

async function deleteWorkflowConfirmed() {
  const workflowId = Number.parseInt(safeString(selectedToDeleteId.value), 10);
  if (Number.isFinite(workflowId) !== true || workflowId <= 0) {
    isDeleteModalOpen.value = false;
    return;
  }
  try {
    await axios.delete(`${apiEndpoint.value}/api/workflows/${workflowId}`);
    workflows.value = workflows.value.filter((item) => Number.parseInt(safeString(item.workflowid), 10) !== workflowId);
    toast.success("Workflow deleted.");
  } catch (error) {
    toast.error("Failed to delete workflow.");
  } finally {
    isDeleteModalOpen.value = false;
    selectedToDeleteId.value = null;
  }
}

function handleCreateWorkflowClick() {
  router.push("/workflowEditor");
}

onMounted(async () => {
  await loadWorkflows();
});
</script>

<style scoped>
.action-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.65rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  color: #4338ca;
  background: #e0e7ff;
  transition: all 0.2s ease;
}

.action-btn > * + * {
  margin-left: 0.35rem;
}

.action-btn:hover {
  background: #c7d2fe;
}

.action-btn.btn-delete {
  color: #b91c1c;
  background: #fee2e2;
}

.action-btn.btn-delete:hover {
  background: #fecaca;
}

.action-btn.btn-edit {
  color: #1d4ed8;
  background: #dbeafe;
  margin-right: 0.5rem;
}

.action-btn.btn-edit:hover {
  background: #bfdbfe;
}

.status-pill {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-active {
  background-color: #ecfdf5;
  color: #059669;
}

.status-disabled {
  background-color: #fef2f2;
  color: #dc2626;
}
</style>
